import { downloadCSV } from '@helpers/downloadCSV';
import { parseEndOfDayFromQueryParam } from '@helpers/parseEndOfDayFromQueryParam';
import { parseStatusesFromQueryParam } from '@helpers/parseStatusesFromQueryParam';
import { parseStartOfDayFromQueryParam } from '@helpers/parseStartOfDayFromQueryParam';
import { serializeDateToQueryParam } from '@helpers/serializeDateToQueryParam';
import { toUnixSecondsQueryParam } from '@helpers/toUnixSecondsQueryParam';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { hashKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { JobResponseDto } from 'app/API';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { getJobsListErrorMessage } from '@helpers/getJobsListErrorMessage';
import { jobsKeys, TWorkspaceJobsListTab } from 'app/queries/jobs/jobsKeys';
import { TPaginatedResponse } from 'app/types/API/TPaginatedResponse';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { StatusFilters } from '../components/Content/components/StatusFilters';
import { TJobsFilter } from 'app/types/TJobsFilter';
import { createFetchWorkspaceJobsQueryFn } from '../api/fetchWorkspaceJobs';
import { requestWorkspaceJobs } from '../api/requestWorkspaceJobs';
import { buildJobsListCsvRows, JOBS_LIST_CSV_HEADERS } from '../helpers/buildJobsListCsvRows';
import { buildWorkspaceJobsListParams } from '../helpers/buildWorkspaceJobsListParams';

const SEARCH_DEBOUNCE_MS = 300;
const EMPTY_JOBS: TPaginatedResponse<JobResponseDto> = { data: [], count: 0 };

type TState = {
  jobs: {
    data: JobResponseDto[];
    count: number;
  };
  setJobs: (v: { data: JobResponseDto[]; count: number }) => void;
  isLoading: boolean;
  errorMessage: string | null;
  isRetryDisabled: boolean;
  retry: () => void;
  reload: () => Promise<void>;
  exportCSV: () => void;
  isExportDisabled: boolean;
  isExporting: boolean;
  currentTab: 'All Jobs' | 'Assigned To Me';
  setCurrentTab: (v: 'All Jobs' | 'Assigned To Me') => void;
  checkedJobs: string[];
  setCheckedJobs: (v: string[]) => void;
  orderBy: string;
  orderType: string;
  statusFilters: StatusFilters;
  setStatusFilters: (v: StatusFilters) => void;
  dateFilter: TJobsFilter;
  setDateFilter: (arg: TJobsFilter | ((v: TJobsFilter) => TJobsFilter)) => void;
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  toggleSort: (field: string) => void;
};

const PageContext = createContext<TState>({
  jobs: {
    data: [],
    count: 0,
  },
  setJobs: () => {},
  isLoading: true,
  errorMessage: null,
  isRetryDisabled: false,
  retry: () => {},
  reload: async () => {},
  exportCSV: () => {},
  isExportDisabled: false,
  isExporting: false,
  currentTab: 'All Jobs',
  setCurrentTab: () => {},
  checkedJobs: [],
  setCheckedJobs: () => {},
  orderBy: 'createdAt',
  orderType: 'desc',
  statusFilters: {
    all: true,
    JOB_STATUS_DRAFT: false,
    JOB_STATUS_PROCESSING: false,
    JOB_STATUS_TRANSCRIBING: false,
    JOB_STATUS_PROOFING: false,
    JOB_STATUS_COMPLETE: false,
    JOB_STATUS_READY_FOR_TRANSCRIBING: false,
    JOB_STATUS_READY_FOR_PROOFREADING: false,
    JOB_STATUS_READY_FOR_FINAL: false,
    JOB_STATUS_FINALIZING: false,
    AS_ONE_LOCKED: false,
  },
  setStatusFilters: () => {},
  dateFilter: {
    from: undefined,
    to: undefined,
  },
  setDateFilter: () => {},
  search: '',
  setSearch: () => {},
  page: 1,
  pageSize: 25,
  setPage: () => null,
  setPageSize: () => {},
  toggleSort: () => {},
});

type TProps = {
  children: ReactNode;
  workspaceId: string;
};

export const PageProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { organization, workspace } = useAuthContext();
  const workspaceId = workspace?.id ?? '';
  const {
    searchParams,
    page,
    pageSize,
    search,
    orderBy,
    orderType,
    setSearch,
    setPage,
    setPageSize,
    toggleSort,
    updateParams,
  } = useListQueryParams({
    defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
  });
  const statusFilterList = useMemo(() => parseStatusesFromQueryParam(searchParams.get('statuses')), [searchParams]);
  const statusFilters = useMemo<StatusFilters>(() => {
    const next: StatusFilters = {
      all: statusFilterList.length === 0,
      JOB_STATUS_DRAFT: statusFilterList.includes('JOB_STATUS_DRAFT'),
      JOB_STATUS_PROCESSING: statusFilterList.includes('JOB_STATUS_PROCESSING'),
      JOB_STATUS_TRANSCRIBING: statusFilterList.includes('JOB_STATUS_TRANSCRIBING'),
      JOB_STATUS_PROOFING: statusFilterList.includes('JOB_STATUS_PROOFING'),
      JOB_STATUS_COMPLETE: statusFilterList.includes('JOB_STATUS_COMPLETE'),
      JOB_STATUS_READY_FOR_TRANSCRIBING: statusFilterList.includes('JOB_STATUS_READY_FOR_TRANSCRIBING'),
      JOB_STATUS_READY_FOR_PROOFREADING: statusFilterList.includes('JOB_STATUS_READY_FOR_PROOFREADING'),
      JOB_STATUS_READY_FOR_FINAL: statusFilterList.includes('JOB_STATUS_READY_FOR_FINAL'),
      JOB_STATUS_FINALIZING: statusFilterList.includes('JOB_STATUS_FINALIZING'),
      AS_ONE_LOCKED: statusFilterList.includes('AS_ONE_LOCKED'),
    };
    return next;
  }, [statusFilterList]);
  const dateFilter = useMemo<TJobsFilter>(() => {
    const from = parseStartOfDayFromQueryParam(searchParams.get('from'));
    const to = parseEndOfDayFromQueryParam(searchParams.get('to'));
    return { from, to };
  }, [searchParams]);

  const defaultTab = ['all', 'assignedAndUnassigned'].includes(String(organization?.permissions?.viewJobs))
    ? 'All Jobs'
    : 'Assigned To Me';
  const tabParam = searchParams.get('tab');
  const currentTab = (tabParam === 'assigned' ? 'Assigned To Me' : tabParam === 'all' ? 'All Jobs' : defaultTab) as
    | 'All Jobs'
    | 'Assigned To Me';
  const [checkedJobs, setCheckedJobs] = useState<string[]>([]);

  const setCurrentTab = (v: 'All Jobs' | 'Assigned To Me') =>
    updateParams({ tab: v === 'Assigned To Me' ? 'assigned' : null }, { resetPage: true });

  const setStatusFilters = (v: StatusFilters) => {
    const statuses = Object.keys(v).filter((k) => k !== 'all' && v[k as keyof StatusFilters]);
    updateParams({ statuses: statuses.length ? statuses.join(',') : null }, { resetPage: true });
  };

  const setDateFilter = (arg: TJobsFilter | ((v: TJobsFilter) => TJobsFilter)) => {
    const next = typeof arg === 'function' ? arg(dateFilter) : arg;
    updateParams(
      {
        from: serializeDateToQueryParam(next.from) || null,
        to: serializeDateToQueryParam(next.to) || null,
      },
      { resetPage: true },
    );
  };

  const setSearchState: React.Dispatch<React.SetStateAction<string>> = (value) => {
    const next = typeof value === 'function' ? value(search) : value;
    setSearch(next);
  };

  const setPageSizeState: React.Dispatch<React.SetStateAction<number>> = (value) => {
    const next = typeof value === 'function' ? value(pageSize) : value;
    setPageSize(next);
  };

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const tab: TWorkspaceJobsListTab = currentTab === 'All Jobs' ? 'all' : 'assigned';
  const status = Object.keys(statusFilters)
    .filter((k) => k !== 'all' && statusFilters[k as keyof typeof statusFilters])
    .join(',');
  const from = toUnixSecondsQueryParam(dateFilter.from);
  const to = toUnixSecondsQueryParam(dateFilter.to);
  const listParams = useMemo(
    () =>
      buildWorkspaceJobsListParams({
        tab,
        page,
        pageSize,
        orderBy,
        orderType,
        search: debouncedSearch,
        status,
        from,
        to,
      }),
    [debouncedSearch, from, orderBy, orderType, page, pageSize, status, tab, to],
  );
  const listKey = useMemo(() => jobsKeys.workspaceList(workspaceId, listParams), [workspaceId, listParams]);
  const listKeyHash = hashKey(listKey);

  const jobsQuery = useQuery({
    queryKey: listKey,
    queryFn: createFetchWorkspaceJobsQueryFn(call),
    placeholderData: (previousData, previousQuery) => {
      const previousKey = previousQuery?.queryKey[0];
      return previousKey?.workspaceId === workspaceId && previousKey.params.tab === tab ? previousData : undefined;
    },
    enabled: workspaceId !== '' && !isSearchSettling,
  });
  const jobs = jobsQuery.data ?? EMPTY_JOBS;

  useEffect(() => {
    setCheckedJobs([]);
  }, [listKeyHash]);

  const setJobs = useCallback(
    (next: TPaginatedResponse<JobResponseDto>) =>
      queryClient.setQueryData<TPaginatedResponse<JobResponseDto>>(listKey, (current) => (current ? next : current)),
    [listKey, queryClient],
  );

  const reload = async () => {
    setCheckedJobs([]);
    await queryClient.invalidateQueries({ queryKey: jobsKeys.listsByWorkspace(workspaceId) });
  };

  const exportMutation = useMutation({
    mutationFn: () =>
      call(() =>
        requestWorkspaceJobs(
          workspaceId,
          buildWorkspaceJobsListParams({
            tab,
            page: 1,
            pageSize: jobs.count,
            orderBy: 'createdAt',
            orderType: 'desc',
            search: debouncedSearch,
            status: '',
            from,
            to,
          }),
        ),
      ),
    onSuccess: (response) =>
      downloadCSV(
        buildJobsListCsvRows(response.data, {
          organizationName: organization?.name ?? '',
          workspaceName: workspace?.name ?? '',
        }),
        JOBS_LIST_CSV_HEADERS,
      ),
    onError: notifyApiCallError,
  });

  const isLoading = jobsQuery.isFetching || isSearchSettling || exportMutation.isPending;
  const isExportDisabled =
    isSearchSettling || jobsQuery.isPlaceholderData || !jobsQuery.isSuccess || exportMutation.isPending;

  useEffect(() => {
    if (currentTab !== defaultTab) {
      setCurrentTab(defaultTab);
    }
  }, [organization?.id]);

  return (
    <PageContext.Provider
      value={{
        jobs,
        setJobs,
        isLoading,
        errorMessage: jobsQuery.isError ? getJobsListErrorMessage(jobsQuery.error) : null,
        isRetryDisabled: jobsQuery.isFetching,
        retry: () => void jobsQuery.refetch(),
        reload,
        exportCSV: () => exportMutation.mutate(),
        isExportDisabled,
        isExporting: exportMutation.isPending,
        currentTab,
        setCurrentTab,
        checkedJobs,
        setCheckedJobs,
        orderBy,
        orderType,
        statusFilters,
        setStatusFilters,
        dateFilter,
        setDateFilter,
        search,
        setSearch: setSearchState,
        page,
        setPage,
        pageSize,
        setPageSize: setPageSizeState,
        toggleSort,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => useContext(PageContext);
